import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// Global site-mode flag — controls whether the public catalog is reachable
// by unauthenticated visitors. When off, the root + every public-facing
// catalog route redirects to /login so customers see a clear sign-in
// surface (instead of the verify-by-WhatsApp gate which confused some).
//
// The flag is fetched once on app boot, cached in module state for the
// rest of the session, and refreshable on demand. Admins also push their
// own toggle through `mutateSiteMode` so the UI reacts instantly without
// waiting for a refetch.

let cached = null;            // null = "not loaded yet"; {public_catalog_enabled:bool}
let inflight = null;          // promise dedup
const listeners = new Set();  // subscribers (React hook instances)

async function fetchSiteMode() {
  if (inflight) return inflight;
  inflight = api.get("/site-mode")
    .then(({ data }) => {
      cached = { public_catalog_enabled: !!data?.public_catalog_enabled };
      listeners.forEach((fn) => fn(cached));
      return cached;
    })
    .catch(() => {
      // On network error default to "enabled" so a backend hiccup never locks
      // out legitimate customers. Admin's toggle will re-apply on next load.
      cached = { public_catalog_enabled: true };
      listeners.forEach((fn) => fn(cached));
      return cached;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function useSiteMode() {
  const [state, setState] = useState(cached);

  useEffect(() => {
    listeners.add(setState);
    if (cached === null) fetchSiteMode();
    return () => { listeners.delete(setState); };
  }, []);

  const refresh = useCallback(() => fetchSiteMode(), []);
  const setMode = useCallback((next) => {
    cached = { public_catalog_enabled: !!next };
    listeners.forEach((fn) => fn(cached));
  }, []);

  return {
    loading: state === null,
    publicCatalogEnabled: state?.public_catalog_enabled ?? true,
    refresh,
    setMode,
  };
}
